import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function cachedPos() {
  try {
    const p = JSON.parse(localStorage.getItem("orio_radar_last_pos_v1") || "null");
    return p && typeof p.lat === "number" ? p : null;
  } catch { return null; }
}

export function usePush() {
  const [status, setStatus] = useState("idle");
  // idle | unsupported | denied | granted-subscribed | granted-unsubscribed | granting

  const supported = typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;

  const refreshStatus = useCallback(async () => {
    if (!supported) {
      setStatus("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    if (Notification.permission !== "granted") {
      setStatus("idle");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setStatus(sub ? "granted-subscribed" : "granted-unsubscribed");
    } catch {
      setStatus("idle");
    }
  }, [supported]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setStatus("granting");
    let perm = Notification.permission;
    if (perm !== "granted") {
      perm = await Notification.requestPermission();
    }
    if (perm !== "granted") {
      setStatus(perm === "denied" ? "denied" : "idle");
      return { ok: false, reason: perm };
    }
    try {
      const { data } = await api.get("/push/vapid-public-key");
      const key = data.public_key;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
      }
      const json = sub.toJSON();
      const pos = cachedPos();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        expirationTime: json.expirationTime || null,
        user_agent: navigator.userAgent,
        ...(pos ? { lat: pos.lat, lng: pos.lng } : {}),
      });
      setStatus("granted-subscribed");
      return { ok: true };
    } catch (e) {
      console.error("push subscribe error", e);
      setStatus("idle");
      return { ok: false, reason: "error" };
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await api.post("/push/unsubscribe", { endpoint }); } catch (_e) { /* ignore */ }
      }
      setStatus("granted-unsubscribed");
    } catch (e) {
      console.error(e);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try { await api.post("/push/test"); return true; } catch { return false; }
  }, []);

  return { status, supported, subscribe, unsubscribe, sendTest, refreshStatus };
}
